import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Grid from '../../../molecules/Grid';
import { TestingAnswers, TestingContainer, TestingMainImage, TestingQuestion } from './styles.js';
import { useSelector, useDispatch } from 'react-redux';
import { answerClick } from '@/store/reducers/testingSlice.js';
import TestingAnswersList from '../TestingAnswersList/TestingAnswersList';
import { useCustomFieldsDynamicSchema, useCustomFieldsExtendValidation } from '@/core/schemes/schemaTestingForm.js';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { MathJaxContext, MathJax } from 'better-react-mathjax';
import mathJaxConfig from '@/constants/mathJaxConfig';
import { sanitizeHTML } from '@/core/hooks/useSanitizedHTML';
import useIsMath from '@/core/hooks/useIsMath';

export type FormDataType = {
    test: string;
};

export type NameType = 'illustrative' | 'basic';

export type TestingFormTypes = {
    type?: NameType;
};

const TestingForm: React.FC<TestingFormTypes> = ({ type = '' }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const { activeQuestion, testData, testId } = useSelector((state: any) => state.testing);
    const dispatch = useDispatch();
    const [animate, setAnimate] = useState(false); // Анимация между вопросами

    const currentQuestionText: string = testData[activeQuestion]?.question?.label || '';
    const currentQuestionImg: string = testData[activeQuestion]?.question?.img || '';
    const sanitizedHTMLQuestion = sanitizeHTML(currentQuestionText);
    const isMath = useIsMath(currentQuestionText);

    const options: any[] = testData[activeQuestion]?.answers?.list || [];
    const currentQuestionId: number = testData[activeQuestion]?.id;
    const currentQuestionNextBtnShow: boolean = testData[activeQuestion]?.question?.nextBtnShow;

    // Расширяем данные для валидации
    const optionsWithNames = options.map((option: any, index: number) => ({
        ...option,
        name: `test_${testId}_${activeQuestion}_${index}`,
    }));
    const dynamicFormData = useCustomFieldsExtendValidation(optionsWithNames);

    // Создаем схему валидации
    const customFieldsSchema = dynamicFormData.reduce(useCustomFieldsDynamicSchema, {});
    const dynamicValidationSchema = Yup.object().shape(customFieldsSchema);

    const methods = useForm<FormDataType>({
        defaultValues: {},
        resolver: yupResolver(dynamicValidationSchema),
        mode: 'all',
    });

    useLayoutEffect(() => {
        setAnimate(true);
    }, [activeQuestion]);

    const onAnswerClick = (val: string | number, type: string) => {
        dispatch(answerClick(val, currentQuestionId, currentQuestionNextBtnShow, type));
        setAnimate(false);
    };

    const Question = <TestingQuestion>
        {/*{activeQuestion + 1}. */}
        <span ref={contentRef} dangerouslySetInnerHTML={{ __html: sanitizedHTMLQuestion }} />
    </TestingQuestion>;

    return (
        <TestingContainer className={animate ? 'animate' : ''}>
            <FormProvider {...methods}>
                <MathJaxContext version={3} config={mathJaxConfig}>
                    <Grid col='1' gap='2' gapDesktop='4' className={type} >
                        {currentQuestionImg && (
                            <TestingMainImage src={currentQuestionImg} alt="" className={type} />
                        )}

                        <TestingQuestion>
                            {isMath ? <MathJax dynamic>{Question}</MathJax> : Question}
                        </TestingQuestion>


                        <TestingAnswers key={activeQuestion}>
                            <TestingAnswersList options={options}
                                                onAnswerClick={onAnswerClick}
                                                name={`test_${testId}`}/>
                        </TestingAnswers>
                    </Grid>
                </MathJaxContext>
            </FormProvider>
        </TestingContainer>
    );
};

export default TestingForm;
